import { useEffect } from "react";
import { BUILD_VERSION } from "@/lib/buildVersion";
import { forceFlushAndReload } from "@/lib/forceFlush";

// One-shot guard so we never enter a reload loop in pathological cases
// (server briefly serves a min_client_build higher than every shipped client,
// the admin accidentally over-bumps it, etc).
const FLUSH_ATTEMPT_KEY = "sv_version_flush_attempt";
const FLUSH_TTL_MS = 5 * 60 * 1000; // 5 min — long enough that the new build
                                    // hits the CDN, short enough that a real
                                    // mis-config still self-corrects later.

/**
 * Version-pin remote kill-switch. On every app mount the frontend asks the
 * backend "what's the minimum build number you'll accept?" If our hard-coded
 * BUILD_VERSION is lower than that, this client is stale → we wipe SW +
 * caches and hard-reload (so the next request to the CDN pulls the new
 * bundle). The admin triggers this by clicking "Flush stale PWA clients" in
 * the admin panel, which bumps `min_client_build` in MongoDB.
 *
 * Failures are silent — a network blip on /api/version must never block the
 * customer from using the catalog.
 */
export function useVersionPin(apiBaseUrl) {
  useEffect(() => {
    if (!apiBaseUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/version`, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "omit",
          cache: "no-store",
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const minBuild = parseInt(data?.min_client_build, 10) || 0;
        if (minBuild <= BUILD_VERSION) return; // we're fresh enough

        // Stale → flush, but only once per FLUSH_TTL_MS window per session
        // storage to prevent infinite reload loops.
        let lastAttempt = 0;
        try { lastAttempt = parseInt(sessionStorage.getItem(FLUSH_ATTEMPT_KEY) || "0", 10) || 0; } catch { /* ignore */ }
        if (Date.now() - lastAttempt < FLUSH_TTL_MS) return;
        try { sessionStorage.setItem(FLUSH_ATTEMPT_KEY, String(Date.now())); } catch { /* quota */ }

        forceFlushAndReload(`server pinned min_client_build=${minBuild}, this client is build ${BUILD_VERSION}`);
      } catch {
        /* offline / DNS / CORS — ignore, customer keeps browsing on local build */
      }
    })();
    return () => { cancelled = true; };
  }, [apiBaseUrl]);
}
